import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import MCQViewer from "./MCQViewer";
import TrueFalseViewer from "./TrueFalseViewer";
import MatchViewer from "./MatchViewer";
import FillUpsViewer from "./FillUpsViewer";
import AnalysisComponent from "./AnalysisComponent";
import Switch from "@mui/material/Switch";
import MDEditor from "@uiw/react-md-editor";
import {
  GENERATE_CONTENT_URL,
  GENERATE_CONTENT_NEW_FORMAT_URL,
  GENERATE_CONTENT_TEACHER_URL,
} from "../../helper/apiurls/apiurls";
import { createHomework } from "../../helper/apicalls/homework";
import "../../styles/common/generateHomeworkPopup.scss";
import Cookies from "js-cookie";

const GenerateHomeworkPopup = ({
  open,
  handleClose,
  onSave,
  context,
  contextKey,
  batchId,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [prompt, setPrompt] = useState("");
  const [generatedItems, setGeneratedItems] = useState(null);
  const [questionType, setQuestionType] = useState("mcq");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    prompt: "",
    name: "",
    deadline: "",
    category: "",
  });
  const [testQuestionCounts, setTestQuestionCounts] = useState({});
  const [testSwitchState, setTestSwitchState] = useState({
    mcq: false,
    trueorfalse: false,
    match: false,
    fillups: false,
    analysis: false,
  });
  const [manualMCQContent, setManualMCQContent] = useState({
    questions: [
      {
        question: "",
        options: ["", "", "", ""],
        correctIndex: 0,
        explanation: "",
      },
    ],
  });
  const [manualTrueFalseContent, setManualTrueFalseContent] = useState({
    questions: [
      {
        question: "",
        answer: "true",
        explanation: "",
      },
    ],
  });
  const [manualMatchContent, setManualMatchContent] = useState({
    pairs: [
      {
        left: "",
        right: "",
      },
    ],
  });
  const [manualFillUpsContent, setManualFillUpsContent] = useState({
    sentences: [
      {
        sentence: "",
        answers: [""],
      },
    ],
  });
  const [manualAnalysisContent, setManualAnalysisContent] = useState({
    title: "",
    content: "",
  });

  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [deadline, setDeadline] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const websocketRef = useRef(null);
  const token = Cookies.get("teacher_token") || Cookies.get("institution_token") || Cookies.get("admin_token") || "";

  const normalizeAnswer = (answer) => {
    if (typeof answer === "boolean") {
      return answer ? "true" : "false";
    }
    if (typeof answer === "string") {
      const lowerAnswer = answer.toLowerCase();
      if (["true", "1", "a", "yes"].includes(lowerAnswer)) return "true";
      if (["false", "0", "b", "no"].includes(lowerAnswer)) return "false";
    }
    return answer;
  };

  const buildContentArrayFromGenerated = (source) => {
    if (!source) return [];
    if (Array.isArray(source)) return source;
    if (source && Array.isArray(source.questions)) return source.questions;
    if (source && source.questions) return [source.questions];
    return [source];
  };

  const handleSaveClick = async () => {
    try {
      if (!batchId) {
        setGenerateError("Please select a batch before saving homework.");
        return;
      }
      if (!name.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          name: "Please enter a name.",
        }));
        return;
      }
      if (!deadline) {
        setFieldErrors((prev) => ({
          ...prev,
          deadline: "Please select a deadline.",
        }));
        return;
      }

      const contentObj =
        activeTab === 0 ? generatedItems : formatManualQuestionsForSave();
      let contentArray = buildContentArrayFromGenerated(contentObj);
      contentArray = contentArray.map((q) => ({
        ...q,
        type: q?.type || questionType,
      }));

      if (activeTab === 1 && category) {
        contentArray = contentArray.map((q) => ({
          ...q,
          categories: q.categories || category,
        }));
      }

      const payload = {
        batch: batchId,
        content: contentArray,
        name,
        deadline: new Date(deadline).toISOString(),
      };

      if (context && contextKey) {
        if (context === "course") payload.course = contextKey;
        else if (context === "subject") payload.subject = contextKey;
        else if (context === "topic") payload.topic = contextKey;
        else if (context === "subtopic" || context === "subtopic") {
          payload.subtopic = contextKey;
        }
      }

      const data = await createHomework(payload, context);
      if (onSave) onSave(data);
      if (handleClose) handleClose();
    } catch (e) {
      setGenerateError(
        e?.message || "Failed to save homework. Please try again.",
      );
    }
  };

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setPrompt("");
      setQuestionType("mcq");
      setGenerateError("");
      setIsGenerating(false);
      setGeneratedItems(null);
      setCategory("");
      setName("");
      setDeadline("");
      setFieldErrors({ prompt: "", name: "", deadline: "", category: "" });
      setTestSwitchState({
        mcq: false,
        trueorfalse: false,
        match: false,
        fillups: false,
        analysis: false,
      });
      setTestQuestionCounts({});
      setManualMCQContent({
        questions: [
          {
            question: "",
            options: ["", "", "", ""],
            correctIndex: 0,
            explanation: "",
          },
        ],
      });
      setManualTrueFalseContent({
        questions: [
          {
            question: "",
            answer: "true",
            explanation: "",
          },
        ],
      });
      setManualMatchContent({
        pairs: [
          {
            left: "",
            right: "",
          },
        ],
      });
      setManualFillUpsContent({
        sentences: [
          {
            sentence: "",
            answers: [""],
          },
        ],
      });
      setManualAnalysisContent({ title: "", content: "" });
    }
  }, [open]);

  if (!open) return null;

  const formatManualQuestionsForSave = () =>
    formatManualQuestionsForSaveBuilder(
      questionType,
      manualMCQContent,
      manualTrueFalseContent,
      manualMatchContent,
      manualFillUpsContent,
      manualAnalysisContent,
    );

  const handleGenerateClick = () => {
    const nextErrors = { ...fieldErrors };
    let hasError = false;
    if (!name.trim()) {
      nextErrors.name = "Please enter a name.";
      hasError = true;
    }
    if (!deadline) {
      nextErrors.deadline = "Please select a deadline.";
      hasError = true;
    }
    if (!category) {
      nextErrors.category = "Please select a category.";
      hasError = true;
    }
    if (!prompt.trim()) {
      nextErrors.prompt = "Please enter a prompt";
      hasError = true;
    }
    if (hasError) {
      setFieldErrors(nextErrors);
      return;
    }

    setIsGenerating(true);
    setGenerateError("");

    try {
      if (websocketRef.current) {
        websocketRef.current.close();
      }

      const role = Cookies.get("role");
      let socketUrl = GENERATE_CONTENT_URL;
      if (role === "institution" || role === "teacher") {
        socketUrl = GENERATE_CONTENT_TEACHER_URL;
      } else {
        socketUrl = GENERATE_CONTENT_NEW_FORMAT_URL;
      }

      websocketRef.current = new WebSocket(socketUrl);

      websocketRef.current.onopen = () => {
        const typesArr = [];
        [
          { key: "mcq" },
          { key: "trueorfalse" },
          { key: "match" },
          { key: "fillups" },
          // { key: "analysis" },
        ].forEach((item) => {
          const enabled = testSwitchState[item.key];
          const countNum = Number(testQuestionCounts[item.key] || 0);
          if (enabled && Number.isFinite(countNum) && countNum > 0) {
            typesArr.push({ type: item.key, count: countNum });
          }
        });

        if (typesArr.length === 0) {
          typesArr.push({ type: questionType || "mcq", count: 1 });
        }

        const message = {
          prompt: prompt.trim(),
          types: typesArr,
          field: context || "homework",
          key: contextKey || "",
          token: token,
        };

        websocketRef.current.send(JSON.stringify(message));
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (Array.isArray(data?.results)) {
            const results = data.results;
            const flattened = [];
            results.forEach((r) => {
              const structured = r?.data?.structured;
              const questions = structured?.questions;
              if (Array.isArray(questions) && questions.length > 0) {
                questions.forEach((q) => flattened.push(q));
              }
            });
            if (flattened.length > 0) {
              setGeneratedItems(flattened);
              return;
            }
            setGeneratedItems(data);
            return;
          }

          if (data && data.data) {
            let contentToSet = data.data.content ?? data.data;
            if (typeof contentToSet === "string") {
              try {
                contentToSet = JSON.parse(contentToSet);
              } catch (parseError) {}
            }
            setGeneratedItems(contentToSet);
          } else {
            let contentToSet = event.data;
            if (typeof contentToSet === "string") {
              try {
                contentToSet = JSON.parse(contentToSet);
              } catch (parseError) {}
            }
            setGeneratedItems(contentToSet);
          }
        } catch (err) {
          let contentToSet = event.data;
          if (typeof contentToSet === "string") {
            try {
              contentToSet = JSON.parse(contentToSet);
            } catch (parseError) {}
          }
          setGeneratedItems(contentToSet);
        }
      };

      websocketRef.current.onerror = () => {
        setGenerateError("Failed to connect to generator service");
        setIsGenerating(false);
      };

      websocketRef.current.onclose = () => {
        setIsGenerating(false);
      };
    } catch (err) {
      setGenerateError("Failed to start generation");
      setIsGenerating(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && handleClose) {
      handleClose();
    }
  };

  const hasManualQuestionData = () => {
    try {
      switch (questionType) {
        case "mcq": {
          const list = Array.isArray(manualMCQContent?.questions)
            ? manualMCQContent.questions
            : [];
          return list.some((q) => {
            const qText = (q?.question || "").trim();
            const hasOption = (q?.options || []).some((opt) => {
              if (typeof opt === "string") return opt.trim().length > 0;
              if (opt && typeof opt === "object")
                return JSON.stringify(opt).length > 2;
              return false;
            });
            return qText.length > 0 || hasOption;
          });
        }
        case "trueorfalse": {
          const list = Array.isArray(manualTrueFalseContent?.questions)
            ? manualTrueFalseContent.questions
            : [];
          return list.some(
            (q) =>
              (q?.question || q?.title || q?.text || "").toString().trim()
                .length > 0,
          );
        }
        case "match": {
          const pairs = Array.isArray(manualMatchContent?.pairs)
            ? manualMatchContent.pairs
            : [];
          return pairs.some(
            (p) =>
              (p?.left || "").trim().length > 0 ||
              (p?.right || "").trim().length > 0,
          );
        }
        case "fillups": {
          const sentences = Array.isArray(manualFillUpsContent?.sentences)
            ? manualFillUpsContent.sentences
            : [];
          return sentences.some((s) => {
            const hasSentence = (s?.sentence || "").trim().length > 0;
            const hasAnswer = (s?.answers || []).some(
              (a) => (a || "").trim().length > 0,
            );
            return hasSentence || hasAnswer;
          });
        }
        case "analysis": {
          return (
            (manualAnalysisContent?.title || "").trim().length > 0 ||
            (manualAnalysisContent?.content || "").trim().length > 0
          );
        }
        default:
          return false;
      }
    } catch (e) {
      return false;
    }
  };

  return (
    <div
      className="hw-generator-popup high-z-index"
      onClick={handleBackdropClick}
    >
      <div className="hw-generator-dialog">
        <div className="hw-generator-header">
          <h2 className="hw-generator-title">Generate Homework</h2>
          <button className="hw-generator-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="hw-generator-tabs">
          <div
            className={`hw-generator-tab ${activeTab === 0 ? "active" : ""}`}
            onClick={() => {
              setActiveTab(0);
              setFieldErrors({
                prompt: "",
                name: "",
                deadline: "",
                category: "",
              });
              setGenerateError("");
            }}
          >
            Generate with AI
          </div>
          <div
            className={`hw-generator-tab ${activeTab === 1 ? "active" : ""}`}
            onClick={() => {
              setActiveTab(1);
              setFieldErrors({
                prompt: "",
                name: "",
                deadline: "",
                category: "",
              });
              setGenerateError("");
            }}
          >
            Manual
          </div>
        </div>

        <div className="hw-generator-content">
          <div
            className="hw-generator-row"
            style={{ display: "flex", gap: 16 }}
          >
            <div className="hw-generator-field" style={{ flex: 1 }}>
              <label className="hw-generator-label">Name</label>
              <input
                type="text"
                className="hw-generator-input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (fieldErrors.name) {
                    setFieldErrors((prev) => ({ ...prev, name: "" }));
                  }
                }}
                placeholder="Enter name"
              />
              {fieldErrors.name && (
                <div className="hw-generator-error">{fieldErrors.name}</div>
              )}
            </div>
            <div className="hw-generator-field" style={{ flex: 1 }}>
              <label className="hw-generator-label">Deadline</label>
              <input
                type="datetime-local"
                className="hw-generator-input"
                value={deadline}
                onChange={(e) => {
                  setDeadline(e.target.value);
                  if (fieldErrors.deadline) {
                    setFieldErrors((prev) => ({ ...prev, deadline: "" }));
                  }
                }}
              />
              {fieldErrors.deadline && (
                <div className="hw-generator-error">{fieldErrors.deadline}</div>
              )}
            </div>
          </div>

          <div
            className="hw-generator-row"
            style={{ display: "flex", gap: 16 }}
          >
            {activeTab === 1 && (
              <div className="hw-generator-field" style={{ flex: 1 }}>
                <label className="hw-generator-label">Content Type</label>
                <Select
                  classNamePrefix="content-generator-select"
                  value={{
                    value: questionType,
                    label:
                      questionType === "mcq"
                        ? "MCQ"
                        : questionType === "trueorfalse"
                          ? "True or False"
                          : questionType === "match"
                            ? "Match"
                            : questionType === "fillups"
                              ? "Fill Ups"
                              : "Analysis",
                  }}
                  onChange={(opt) => setQuestionType(opt ? opt.value : "mcq")}
                  options={[
                    { value: "mcq", label: "MCQ" },
                    { value: "trueorfalse", label: "True or False" },
                    { value: "match", label: "Match" },
                    { value: "fillups", label: "Fill Ups" },
                    // { value: "analysis", label: "Analysis" },
                  ]}
                  placeholder="Select content type"
                />
              </div>
            )}

            <div
              className="hw-generator-field"
              style={{ flex: 1, minWidth: 260 }}
            >
              <label className="hw-generator-label">Categories</label>
              <Select
                classNamePrefix="content-generator-select"
                value={
                  category
                    ? {
                        value: category,
                        label: category
                          .replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()),
                      }
                    : null
                }
                onChange={(opt) => {
                  const val = opt ? opt.value : "";
                  setCategory(val);
                  if (fieldErrors.category) {
                    setFieldErrors((prev) => ({ ...prev, category: "" }));
                  }
                }}
                options={[
                  { value: "direct", label: "Direct" },
                  { value: "critical_thinking", label: "Critical Thinking" },
                  { value: "reasoning", label: "Reasoning" },
                  { value: "application", label: "Application" },
                ]}
                placeholder="Select a category"
              />
              {fieldErrors.category && (
                <div className="hw-generator-error">{fieldErrors.category}</div>
              )}
            </div>
          </div>
          {activeTab === 0 ? (
            <div className="hw-generator-tab-content active">
              <div className="input-section">
                <div className="prompt-section">
                  <textarea
                    className="prompt-textarea"
                    placeholder="Enter a prompt to generate homework items"
                    value={prompt}
                    onChange={(e) => {
                      setPrompt(e.target.value);
                      if (fieldErrors.prompt) {
                        setFieldErrors((prev) => ({ ...prev, prompt: "" }));
                      }
                    }}
                  />
                  {fieldErrors.prompt && (
                    <div className="hw-generator-error">
                      {fieldErrors.prompt}
                    </div>
                  )}
                </div>

                <div className="hw-question-types">
                  <div className="hw-question-types-label hw-generator-label">
                    Question Types & Count
                  </div>
                  {[
                    { key: "mcq", label: "MCQ" },
                    { key: "trueorfalse", label: "True or False" },
                    { key: "match", label: "Match" },
                    { key: "fillups", label: "Fill Ups" },
                    // { key: "analysis", label: "Analysis" },
                  ].map((item) => (
                    <div key={item.key} className="hw-question-type-row">
                      <div className="hw-question-type-name">{item.label}</div>
                      <Switch
                        size="small"
                        checked={!!testSwitchState[item.key]}
                        onChange={() =>
                          setTestSwitchState((prev) => ({
                            ...prev,
                            [item.key]: !prev[item.key],
                          }))
                        }
                      />
                      <input
                        type="number"
                        min="1"
                        className={`hw-question-type-count-input ${
                          testSwitchState[item.key] ? "visible" : "hidden"
                        }`}
                        placeholder="No.of Count"
                        value={(testQuestionCounts[item.key] ?? "").toString()}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          setTestQuestionCounts((prev) => ({
                            ...prev,
                            [item.key]: value,
                          }));
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Preview moved to a separate popup - trigger via button below */}
              </div>
            </div>
          ) : (
            <div className="hw-generator-tab-content active">
              <div className="input-section">
                {questionType === "mcq" && (
                  <div className="manual-mcq-content">
                    <h3 className="mcq-title">MCQ Questions</h3>
                    {manualMCQContent.questions.map((q, index) => (
                      <div key={index} className="mcq-question">
                        <input
                          className="question-input"
                          placeholder={`Question ${index + 1}`}
                          value={q.question || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setManualMCQContent((prev) => ({
                              ...prev,
                              questions: prev.questions.map((qq, i) =>
                                i === index ? { ...qq, question: val } : qq,
                              ),
                            }));
                          }}
                        />
                        {(q.options || []).map((option, optIndex) => {
                          let optionText = "";
                          if (typeof option === "object" && option !== null) {
                            if (Object.keys(option).length === 1) {
                              const firstValue = Object.values(option)[0];
                              optionText =
                                typeof firstValue === "string"
                                  ? firstValue
                                  : JSON.stringify(firstValue);
                            } else {
                              optionText =
                                option.text ||
                                option.option ||
                                option.value ||
                                option.answer ||
                                JSON.stringify(option);
                            }
                          } else {
                            optionText = option;
                          }
                          return (
                            <div key={optIndex} className="option-row">
                              <input
                                type="radio"
                                className="option-radio"
                                checked={q.correctIndex === optIndex}
                                onChange={() => {
                                  setManualMCQContent((prev) => ({
                                    ...prev,
                                    questions: prev.questions.map((qq, i) =>
                                      i === index
                                        ? { ...qq, correctIndex: optIndex }
                                        : qq,
                                    ),
                                  }));
                                }}
                              />
                              <input
                                className="option-input"
                                placeholder={`Option ${String.fromCharCode(
                                  65 + optIndex,
                                )}`}
                                value={optionText}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setManualMCQContent((prev) => ({
                                    ...prev,
                                    questions: prev.questions.map((qq, i) => {
                                      if (i !== index) return qq;
                                      const opts = [...(qq.options || [])];
                                      opts[optIndex] = val;
                                      return { ...qq, options: opts };
                                    }),
                                  }));
                                }}
                              />
                              <button
                                className="option-delete-btn"
                                onClick={() => {
                                  setManualMCQContent((prev) => ({
                                    ...prev,
                                    questions: prev.questions.map((qq, i) => {
                                      if (i !== index) return qq;
                                      const opts = (qq.options || []).filter(
                                        (_, oi) => oi !== optIndex,
                                      );
                                      let newCorrectIndex = qq.correctIndex;
                                      if (optIndex < qq.correctIndex) {
                                        newCorrectIndex = Math.max(
                                          0,
                                          qq.correctIndex - 1,
                                        );
                                      } else if (optIndex === qq.correctIndex) {
                                        newCorrectIndex = Math.min(
                                          opts.length - 1,
                                          qq.correctIndex,
                                        );
                                      }
                                      return {
                                        ...qq,
                                        options: opts,
                                        correctIndex: newCorrectIndex,
                                      };
                                    }),
                                  }));
                                }}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                        <button
                          className="add-option-btn"
                          onClick={() => {
                            setManualMCQContent((prev) => ({
                              ...prev,
                              questions: prev.questions.map((qq, i) =>
                                i === index
                                  ? {
                                      ...qq,
                                      options: [...(qq.options || []), ""],
                                    }
                                  : qq,
                              ),
                            }));
                          }}
                        >
                          + Add Option
                        </button>
                        <textarea
                          className="explanation-textarea"
                          placeholder="Explanation"
                          value={q.explanation || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setManualMCQContent((prev) => ({
                              ...prev,
                              questions: prev.questions.map((qq, i) =>
                                i === index ? { ...qq, explanation: val } : qq,
                              ),
                            }));
                          }}
                        />
                        {manualMCQContent.questions.length > 1 && (
                          <div style={{ marginTop: 16 }}>
                            <button
                              className="remove-question-btn"
                              onClick={() => {
                                if (manualMCQContent.questions.length > 1) {
                                  setManualMCQContent((prev) => ({
                                    ...prev,
                                    questions: prev.questions.filter(
                                      (_, i) => i !== index,
                                    ),
                                  }));
                                }
                              }}
                            >
                              Remove Question
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      className="add-question-btn"
                      onClick={() => {
                        setManualMCQContent((prev) => ({
                          ...prev,
                          questions: [
                            ...prev.questions,
                            {
                              question: "",
                              options: ["", "", "", ""],
                              correctIndex: 0,
                              explanation: "",
                            },
                          ],
                        }));
                      }}
                    >
                      + Add Question
                    </button>
                  </div>
                )}
                {questionType === "trueorfalse" && (
                  <div className="manual-truefalse-content">
                    <h3 className="tf-title">True/False Questions</h3>
                    {manualTrueFalseContent.questions.map((q, index) => {
                      const normalizedCorrectAnswer = normalizeAnswer(
                        q.answer || q.correct_answer || q.correct || "true",
                      );
                      return (
                        <div key={index} className="tf-question">
                          <input
                            className="question-input"
                            placeholder={`Question ${index + 1}`}
                            value={q.question || q.title || q.text || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setManualTrueFalseContent((prev) => ({
                                ...prev,
                                questions: prev.questions.map((qq, i) =>
                                  i === index ? { ...qq, question: val } : qq,
                                ),
                              }));
                            }}
                          />
                          <div className="tf-answer">
                            <div
                              className={`tf-option ${
                                normalizedCorrectAnswer === "true"
                                  ? "correct"
                                  : ""
                              }`}
                              onClick={() => {
                                setManualTrueFalseContent((prev) => ({
                                  ...prev,
                                  questions: prev.questions.map((qq, i) =>
                                    i === index
                                      ? {
                                          ...qq,
                                          answer: "true",
                                          correct_answer: "true",
                                        }
                                      : qq,
                                  ),
                                }));
                              }}
                            >
                              <input
                                type="radio"
                                className="tf-radio"
                                checked={normalizedCorrectAnswer === "true"}
                                onChange={() => {}}
                              />
                              <span>True</span>
                            </div>
                            <div
                              className={`tf-option ${
                                normalizedCorrectAnswer === "false"
                                  ? "correct"
                                  : ""
                              }`}
                              onClick={() => {
                                setManualTrueFalseContent((prev) => ({
                                  ...prev,
                                  questions: prev.questions.map((qq, i) =>
                                    i === index
                                      ? {
                                          ...qq,
                                          answer: "false",
                                          correct_answer: "false",
                                        }
                                      : qq,
                                  ),
                                }));
                              }}
                            >
                              <input
                                type="radio"
                                className="tf-radio"
                                checked={normalizedCorrectAnswer === "false"}
                                onChange={() => {}}
                              />
                              <span>False</span>
                            </div>
                          </div>
                          <textarea
                            className="explanation-textarea"
                            placeholder="Explanation"
                            value={q.explanation || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setManualTrueFalseContent((prev) => ({
                                ...prev,
                                questions: prev.questions.map((qq, i) =>
                                  i === index
                                    ? { ...qq, explanation: val }
                                    : qq,
                                ),
                              }));
                            }}
                          />
                          {manualTrueFalseContent.questions.length > 1 && (
                            <div style={{ marginTop: 16 }}>
                              <button
                                className="remove-question-btn"
                                onClick={() => {
                                  if (
                                    manualTrueFalseContent.questions.length > 1
                                  ) {
                                    setManualTrueFalseContent((prev) => ({
                                      ...prev,
                                      questions: prev.questions.filter(
                                        (_, i) => i !== index,
                                      ),
                                    }));
                                  }
                                }}
                              >
                                Remove Question
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button
                      className="add-question-btn"
                      onClick={() => {
                        setManualTrueFalseContent((prev) => ({
                          ...prev,
                          questions: [
                            ...prev.questions,
                            { question: "", answer: "true", explanation: "" },
                          ],
                        }));
                      }}
                    >
                      + Add Question
                    </button>
                  </div>
                )}
                {questionType === "match" && (
                  <div className="manual-match-content">
                    <h3 className="match-title">Match Pairs</h3>
                    {manualMatchContent.pairs.map((pair, index) => (
                      <div key={index} className="match-pair">
                        <input
                          className="match-left-input"
                          placeholder="Left side"
                          value={pair.left}
                          onChange={(e) =>
                            setManualMatchContent((prev) => ({
                              ...prev,
                              pairs: prev.pairs.map((p, i) =>
                                i === index
                                  ? { ...p, left: e.target.value }
                                  : p,
                              ),
                            }))
                          }
                        />
                        <input
                          className="match-right-input"
                          placeholder="Right side"
                          value={pair.right}
                          onChange={(e) =>
                            setManualMatchContent((prev) => ({
                              ...prev,
                              pairs: prev.pairs.map((p, i) =>
                                i === index
                                  ? { ...p, right: e.target.value }
                                  : p,
                              ),
                            }))
                          }
                        />
                        {manualMatchContent.pairs.length > 1 && (
                          <button
                            className="remove-pair-btn"
                            onClick={() => {
                              if (manualMatchContent.pairs.length > 1) {
                                setManualMatchContent((prev) => ({
                                  ...prev,
                                  pairs: prev.pairs.filter(
                                    (_, i) => i !== index,
                                  ),
                                }));
                              }
                            }}
                          >
                            Remove Pair
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="add-pair-btn"
                      onClick={() => {
                        setManualMatchContent((prev) => ({
                          ...prev,
                          pairs: [...prev.pairs, { left: "", right: "" }],
                        }));
                      }}
                    >
                      + Add Pair
                    </button>
                  </div>
                )}
                {questionType === "fillups" && (
                  <div className="manual-fillups-content">
                    <h3 className="fillups-title">Fill in the Blanks</h3>
                    {manualFillUpsContent.sentences.map((sentence, index) => (
                      <div key={index} className="fillups-sentence">
                        <textarea
                          className="sentence-textarea"
                          placeholder="Sentence with blanks represented by underscores, e.g., 'The capital of France is ____.'"
                          value={sentence.sentence}
                          onChange={(e) =>
                            setManualFillUpsContent((prev) => ({
                              ...prev,
                              sentences: prev.sentences.map((s, i) =>
                                i === index
                                  ? { ...s, sentence: e.target.value }
                                  : s,
                              ),
                            }))
                          }
                        />
                        <div className="answers-section">
                          <h4>Answers:</h4>
                          {sentence.answers.map((answer, ansIndex) => (
                            <div key={ansIndex} className="answer-input">
                              <input
                                type="text"
                                placeholder={`Answer ${ansIndex + 1}`}
                                value={answer}
                                onChange={(e) =>
                                  setManualFillUpsContent((prev) => ({
                                    ...prev,
                                    sentences: prev.sentences.map((s, i) =>
                                      i === index
                                        ? {
                                            ...s,
                                            answers: s.answers.map((a, j) =>
                                              j === ansIndex
                                                ? e.target.value
                                                : a,
                                            ),
                                          }
                                        : s,
                                    ),
                                  }))
                                }
                              />
                              {sentence.answers.length > 1 && (
                                <button
                                  className="remove-answer-btn"
                                  onClick={() => {
                                    if (sentence.answers.length > 1) {
                                      setManualFillUpsContent((prev) => ({
                                        ...prev,
                                        sentences: prev.sentences.map((s, i) =>
                                          i === index
                                            ? {
                                                ...s,
                                                answers: s.answers.filter(
                                                  (_, j) => j !== ansIndex,
                                                ),
                                              }
                                            : s,
                                        ),
                                      }));
                                    }
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            className="add-answer-btn"
                            onClick={() =>
                              setManualFillUpsContent((prev) => ({
                                ...prev,
                                sentences: prev.sentences.map((s, i) =>
                                  i === index
                                    ? { ...s, answers: [...s.answers, ""] }
                                    : s,
                                ),
                              }))
                            }
                          >
                            + Add Answer
                          </button>
                        </div>
                        {manualFillUpsContent.sentences.length > 1 && (
                          <button
                            className="remove-sentence-btn"
                            onClick={() => {
                              if (manualFillUpsContent.sentences.length > 1) {
                                setManualFillUpsContent((prev) => ({
                                  ...prev,
                                  sentences: prev.sentences.filter(
                                    (_, i) => i !== index,
                                  ),
                                }));
                              }
                            }}
                          >
                            Remove Sentence
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="add-sentence-btn"
                      onClick={() =>
                        setManualFillUpsContent((prev) => ({
                          ...prev,
                          sentences: [
                            ...prev.sentences,
                            { sentence: "", answers: [""] },
                          ],
                        }))
                      }
                    >
                      + Add Sentence
                    </button>
                  </div>
                )}
                {questionType === "analysis" && (
                  <div className="manual-analysis-content">
                    <h3 className="analysis-title">Analysis Content</h3>
                    <input
                      className="analysis-title-input"
                      placeholder="Analysis Title"
                      value={manualAnalysisContent.title}
                      onChange={(e) =>
                        setManualAnalysisContent((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                    />
                    <MDEditor
                      value={manualAnalysisContent.content}
                      onChange={(value) =>
                        setManualAnalysisContent((prev) => ({
                          ...prev,
                          content: value || "",
                        }))
                      }
                      preview="live"
                      height={300}
                    />
                  </div>
                )}
              </div>
              {/* Preview moved to a separate popup - trigger via button below */}
            </div>
          )}
        </div>

        <div className="hw-generator-actions">
          {activeTab === 0 ? (
            <>
              <button
                className="hw-action-btn primary"
                disabled={isGenerating}
                onClick={handleGenerateClick}
              >
                {isGenerating ? "Generating..." : "Generate"}
              </button>
              <button
                className="hw-action-btn primary"
                onClick={() => setIsPreviewOpen(true)}
                disabled={!generatedItems}
                style={
                  !generatedItems ? { opacity: 0.6, cursor: "not-allowed" } : {}
                }
              >
                Preview
              </button>
              <button
                className="hw-action-btn primary"
                onClick={handleSaveClick}
                disabled={!generatedItems}
                style={
                  !generatedItems ? { opacity: 0.6, cursor: "not-allowed" } : {}
                }
              >
                Save
              </button>
            </>
          ) : (
            <>
              <button
                className="hw-action-btn primary"
                onClick={() => setIsPreviewOpen(true)}
                disabled={!hasManualQuestionData()}
                style={
                  !hasManualQuestionData()
                    ? { opacity: 0.6, cursor: "not-allowed" }
                    : {}
                }
              >
                Preview
              </button>
              <button
                className="hw-action-btn primary"
                onClick={handleSaveClick}
                disabled={!name.trim() || !deadline || !hasManualQuestionData()}
                style={
                  !name.trim() || !deadline || !hasManualQuestionData()
                    ? { opacity: 0.6, cursor: "not-allowed" }
                    : {}
                }
              >
                Save
              </button>
            </>
          )}
          {/* <button className="hw-action-btn" onClick={handleClose}>
            Cancel
          </button> */}
          {generateError && (
            <div className="hw-generator-error">{generateError}</div>
          )}
        </div>

        {isPreviewOpen && (
          <div
            className="hw-generator-popup high-z-index"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsPreviewOpen(false);
            }}
          >
            <div className="hw-generator-dialog">
              <div className="hw-generator-header">
                <h2 className="hw-generator-title">Homework Preview</h2>
                <button
                  className="hw-generator-close"
                  onClick={() => setIsPreviewOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="hw-generator-content">
                <div className="hw-preview-section">
                  {activeTab === 0 ? (
                    Array.isArray(generatedItems) ? (
                      generatedItems.map((item, index) => {
                        const itemType = item?.type || questionType;
                        const itemContent = item;
                        switch (itemType) {
                          case "mcq":
                            return (
                              <MCQViewer
                                key={`mcq-prev-${index}`}
                                questionData={itemContent}
                                title="Generated MCQ Homework"
                              />
                            );
                          case "trueorfalse":
                            return (
                              <TrueFalseViewer
                                key={`tf-prev-${index}`}
                                questionData={itemContent}
                                title="Generated True/False Homework"
                              />
                            );
                          case "match":
                            return (
                              <MatchViewer
                                key={`match-prev-${index}`}
                                questionData={itemContent}
                                title="Generated Match Homework"
                              />
                            );
                          case "fillups":
                            return (
                              <FillUpsViewer
                                key={`fillups-prev-${index}`}
                                questionData={itemContent}
                                title="Generated Fill Ups Homework"
                              />
                            );
                          case "analysis":
                            return (
                              <AnalysisComponent
                                key={`analysis-prev-${index}`}
                                data={itemContent}
                                title="Generated Analysis Homework"
                              />
                            );
                          default:
                            return null;
                        }
                      })
                    ) : questionType === "mcq" ? (
                      <MCQViewer
                        questionData={generatedItems}
                        title="Generated MCQ Homework"
                      />
                    ) : questionType === "trueorfalse" ? (
                      <TrueFalseViewer
                        questionData={generatedItems}
                        title="Generated True/False Homework"
                      />
                    ) : questionType === "match" ? (
                      <MatchViewer
                        questionData={generatedItems}
                        title="Generated Match Homework"
                      />
                    ) : questionType === "fillups" ? (
                      <FillUpsViewer
                        questionData={generatedItems}
                        title="Generated Fill Ups Homework"
                      />
                    ) : (
                      <AnalysisComponent
                        data={generatedItems}
                        title="Generated Analysis Homework"
                      />
                    )
                  ) : questionType === "mcq" ? (
                    <MCQViewer
                      questionData={formatManualQuestionsForSave()}
                      title="Manual MCQ Homework Preview"
                    />
                  ) : questionType === "trueorfalse" ? (
                    <TrueFalseViewer
                      questionData={formatManualQuestionsForSave()}
                      title="Manual True/False Homework Preview"
                    />
                  ) : questionType === "match" ? (
                    <MatchViewer
                      questionData={formatManualQuestionsForSave()}
                      title="Manual Match Homework Preview"
                    />
                  ) : questionType === "fillups" ? (
                    <FillUpsViewer
                      questionData={formatManualQuestionsForSave()}
                      title="Manual Fill Ups Homework Preview"
                    />
                  ) : questionType === "analysis" ? (
                    <AnalysisComponent
                      data={formatManualQuestionsForSave()}
                      title="Manual Analysis Homework Preview"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function formatManualQuestionsForSaveBuilder(
  type,
  manualMCQContent,
  manualTrueFalseContent,
  manualMatchContent,
  manualFillUpsContent,
  manualAnalysisContent,
) {
  switch (type) {
    case "mcq": {
      const formattedMCQ = {
        questions: (manualMCQContent.questions || []).map((q) => {
          const options = (q.options || []).map((option, index) => {
            const label = String.fromCharCode(65 + index);
            return { [label]: option };
          });
          const correctLabel = String.fromCharCode(65 + (q.correctIndex || 0));
          return {
            question: q.question || "",
            options,
            answer: correctLabel,
            explanation: q.explanation || "",
          };
        }),
      };
      return formattedMCQ;
    }
    case "trueorfalse": {
      return {
        questions: (manualTrueFalseContent.questions || []).map((q) => ({
          question: q.question || "",
          answer: q.answer || "true",
          explanation: q.explanation || "",
        })),
      };
    }
    case "match": {
      const pairs = manualMatchContent.pairs || [];
      const leftValues = pairs.map((p) => p.left || "");
      const rightValues = pairs.map((p) => p.right || "");
      return {
        questions: [
          { key: "Column 1", values: leftValues },
          { key: "Column 2", values: rightValues },
        ],
      };
    }
    case "fillups": {
      return {
        questions: (manualFillUpsContent.sentences || []).map((s) => ({
          question: s.sentence || "",
          answer:
            (Array.isArray(s.answers) && s.answers.length > 0
              ? s.answers[0]
              : "") || "",
        })),
      };
    }
    case "analysis": {
      return {
        questions: [
          {
            question: manualAnalysisContent.title || "",
            explanation: manualAnalysisContent.content || "",
          },
        ],
      };
    }
    default:
      return {};
  }
}

export default GenerateHomeworkPopup;
